import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Checkbox, IconButton, InputAdornment, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { BadgeOutlined, Clear, PrintOutlined, Search } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { idCardService } from '../../services/idCardService';
import type { User } from '../../types';
import { useDebounce } from '../../hooks/useDebounce';
import EmptyState from '../../components/common/EmptyState';
import FilterBar from '../../components/common/FilterBar';
import SearchableSelect from '../../components/common/SearchableSelect';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import PersonCell from '../../components/people/PersonCell';
import { tableHeadCellSx } from '../../components/admissions/tabLayout';
import { personFromStaff, type CardPerson } from '../../components/idCards/cardPeople';
import { useCardSelection } from '../../components/idCards/useCardSelection';
import CardsToolbar from '../../components/idCards/CardsToolbar';
import PrintCardsDialog, { MAX_PRINT } from '../../components/idCards/PrintCardsDialog';

const PAGE_FETCH = 200;

/**
 * Staff ID cards — every active employee, by role. Cards show initials
 * until staff photos are recorded.
 */
const StaffIdCardsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [roles, setRoles] = useState<Array<{ _id: string; name: string }>>([]);
  const [role, setRole] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const { selected, toggle, togglePage, clear } = useCardSelection();
  const [print, setPrint] = useState<{ load: () => Promise<CardPerson[]>; scope: string } | null>(null);

  useEffect(() => {
    idCardService.staffRoles()
      .then(setRoles)
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the roles', { variant: 'error' }));
  }, [enqueueSnackbar]);

  useEffect(() => { setPage(0); clear(); }, [role, search, clear]);

  const filter = useMemo(() => ({ role: role || undefined, search: search.trim() || undefined }), [role, search]);

  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    idCardService.listStaff({ ...filter, page: page + 1, limit: rowsPerPage })
      .then(res => {
        if (mine !== seq.current) return;
        setUsers(res.users);
        setTotal(res.pagination.totalItems);
      })
      .catch((err: any) => { if (mine === seq.current) { enqueueSnackbar(err?.message || 'Could not load the employees', { variant: 'error' }); setUsers([]); } })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [filter, page, rowsPerPage, enqueueSnackbar]);

  const pagePeople = useMemo(() => users.map(personFromStaff), [users]);
  const pageTicked = pagePeople.length > 0 && pagePeople.every(p => selected.has(p.key));
  const pageSome = pagePeople.some(p => selected.has(p.key));

  const loadAll = useCallback(async () => {
    const out: CardPerson[] = [];
    for (let p = 1; out.length < MAX_PRINT; p++) {
      const res = await idCardService.listStaff({ ...filter, page: p, limit: PAGE_FETCH });
      out.push(...res.users.map(personFromStaff));
      if (!res.pagination.hasNext) break;
    }
    return out;
  }, [filter]);

  const roleName = roles.find(r => r._id === role)?.name;
  const roleOptions = useMemo(() => roles.map(r => ({ value: r._id, label: r.name })), [roles]);
  const filtersActive = !!role || !!search.trim();

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Staff ID cards</Typography>
        <Typography variant="body2" color="text.secondary">
          Print cards for everyone, a role or the employees you choose. Cards show initials until staff photos are added.
        </Typography>
      </Box>

      <FilterBar loading={loading && users.length > 0}>
        <TextField
          size="small"
          placeholder="Search name, staff ID or email"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: 220, maxWidth: 360 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
            endAdornment: searchInput ? (
              <InputAdornment position="end"><IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton></InputAdornment>
            ) : undefined,
          }}
        />
        <SearchableSelect label="Role" placeholder="All roles" value={role} onChange={setRole} options={roleOptions} minWidth={180} />
      </FilterBar>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <CardsToolbar
          selectedCount={selected.size}
          total={total}
          noun={{ one: 'employee', many: 'employees' }}
          onClear={clear}
          onPrintSelected={() => { const list = Array.from(selected.values()); setPrint({ load: async () => list, scope: `${list.length} selected ${list.length === 1 ? 'employee' : 'employees'}` }); }}
          onPrintAll={() => setPrint({ load: loadAll, scope: roleName ? `Role: ${roleName}` : "All staff" })}
        />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': tableHeadCellSx }}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={pageTicked} indeterminate={!pageTicked && pageSome} onChange={() => togglePage(pagePeople)} inputProps={{ 'aria-label': 'Tick every employee on this page' }} />
                </TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Staff ID</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && users.length === 0 ? (
                <TableRowSkeletons columns={6} rows={8} />
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<BadgeOutlined />}
                      title={filtersActive ? 'No employees match' : 'No active employees'}
                      description={filtersActive ? 'Try another name or role, or clear the filters.' : 'Employees appear here once they are added.'}
                      action={filtersActive ? { label: 'Clear filters', onClick: () => { setRole(''); setSearchInput(''); } } : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : users.map((u, i) => {
                const person = pagePeople[i];
                const ticked = selected.has(person.key);
                return (
                  <TableRow key={u._id} hover selected={ticked} onClick={() => toggle(person)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={ticked} inputProps={{ 'aria-label': `Tick ${u.name}` }} />
                    </TableCell>
                    <TableCell><PersonCell name={u.name} secondary={u.email} /></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{u.userCode || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" noWrap>{u.roleId?.name || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{u.phone || '—'}</Typography></TableCell>
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Print this card">
                        <IconButton size="small" onClick={() => setPrint({ load: async () => [person], scope: u.name })} aria-label={`Print ${u.name}'s card`}>
                          <PrintOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {print && <PrintCardsDialog audience="staff" loadPeople={print.load} scope={print.scope} onClose={() => setPrint(null)} />}
    </Box>
  );
};

export default StaffIdCardsPage;
